<template>
    <form class="form-row" @submit.prevent="changePassword()" autocomplete="off">
        <div class="col-md-12"
            :class="{'px-0' : $breakpoint.smAndDown}">
            <h2 class="heading-medium mb-4">Reset Password</h2>
            
            <div :class="{'w-50': !$breakpoint.lgAndDown && isUserLoggedIn, 
                          'w-75' : $breakpoint.mdOnly && isUserLoggedIn, 
                          'w-100' : $breakpoint.smAndDown && isUserLoggedIn }"
            >
                <!-- CURRENT PASSWORD -->
                <div v-if="isUserLoggedIn" class="form-group" :class="{'px-0' : $breakpoint.smAndDown}">
                    <label :class="{'error': password.current.isInvalid}">Current password</label>
                    <input name="currentPassword" class="form-control" placeholder="Enter current password" 
                        v-model="password.current.value" @blur="validatePassword('CURRENT')" :type="password.current.isVisible ? 'text' : 'password'">
                    <div class="invalid-input">
                        <i class="fas fa-exclamation"></i>
                        Incorrect password
                    </div>
                    <i class="fas password-visibility" :class="password.current.isVisible ? 'fa-eye' : 'fa-eye-slash'" @click="password.current.isVisible = !password.current.isVisible"></i>
                </div>

                <!-- NEW PASSWORD -->
                <div class="form-group" :class="{'px-0' : $breakpoint.smAndDown}">
                    <label>New password</label>
                    <input name="newPassword" class="form-control" placeholder="Enter new password" 
                        v-model="password.new.value" @input="validatePassword('NEW')" @blur="validatePassword('NEW')" :type="password.new.isVisible ? 'text' : 'password'">
                    <i class="fas password-visibility" :class="password.new.isVisible ? 'fa-eye' : 'fa-eye-slash'" @click="password.new.isVisible = !password.new.isVisible"></i>

                    <!-- Validation checkboxes -->
                    <div class="mt-3">
                        <div v-for="(req, idx) in passwordRequirements" :key="idx" class="w-100">
                            <i class="fas fa-check-circle mr-2 my-1" :class="{'text-primary': req.fulfilled}"></i>{{ req.text }}
                        </div>
                    </div>
                </div>

                <!-- PASSWORD CONFIRMATION -->
                <div class="form-group" :class="{'px-0' : $breakpoint.smAndDown}">
                    <label :class="{'error': password.confirmation.isInvalid}">Confirm new password</label>
                    <input name="confirmPassword" class="form-control" placeholder="Re-enter new password" 
                        v-model="password.confirmation.value" @blur="validatePassword('CONFIRMATION')" :type="password.confirmation.isVisible ? 'text' : 'password'">
                    <div class="invalid-input">
                        <i class="fas fa-exclamation"></i>
                        Passwords entered do not match
                    </div>
                    <i class="fas password-visibility" :class="password.current.isVisible ? 'fa-eye' : 'fa-eye-slash'" @click="password.confirmation.isVisible = !password.confirmation.isVisible"></i>
                </div>
                
                <div class="form-group mt-5 px-0 text-right col-md-12">
                    <button type="submit" class="btn btn-primary" :disabled="!passwordSequenceComplete">Change password</button>
                    <p v-if="connectionError" class="text-danger pt-2">Sorry, we are unable to process your request currently. Please try again later.</p>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import breakpoint from '@/mixins/index';
import { mapActions } from "vuex";
import { _isInputValid } from "@/util/validation.js";

export default {
    mixins: [breakpoint],
    props: {
        isUserLoggedIn: Boolean
    },
    data(){
        return {
            password: {
                current: {
                    value: "",
                    isInvalid: false,
                    isVisible: false
                },
                new: {
                    value: "",
                    isInvalid: false,
                    isVisible: false
                },
                confirmation: {
                    value: "",
                    isInvalid: false,
                    isVisible: false
                }
            },
            passwordRequirements: [
                {
                    text: "At least 8 characters long",
                    pattern: /.{8,}/,
                    fulfilled: false
                },
                {
                    text: "At least one lowercase letter",
                    pattern: /(?=.*[a-z])/,
                    fulfilled: false
                },
                {
                    text: "At least one uppercase letter",
                    pattern: /(?=.*[A-Z])/,
                    fulfilled: false
                },
                {
                    text: "At least one number",
                    pattern: /(?=.*\d)/,
                    fulfilled: false
                }
            ],
            connectionError: false
        }
    },
    computed: {
        passwordSequenceComplete: {
            get: function(){
                return (this.password.current.value || !this.isUserLoggedIn) && // Has the user filled in the current password field?
                    !this.passwordRequirements.find(req => !req.fulfilled) && // Are all new password requirements satisfied?
                    this.password.new.value === this.password.confirmation.value; // Are the new passwords matching?
            }
        }
    },
    methods: {
        ...mapActions('notifications', ['displayActionConfirmation']),
        validatePassword: function(field){
            switch (field){
                case "CURRENT": // Current passsword needs to be filled
                    this.password.current.isInvalid = this.password.current.value.length < 1;
                    break;
                case "NEW": // New password must match all requirements
                    for (let req of this.passwordRequirements){
                        req.fulfilled = _isInputValid(this.password.new.value, "PASSWORD", req.pattern); // Check whether this requirement is fulfilled
                    }

                    // Can we find an unfulfilled requirement in the requirements list?
                    const unfulfilledReq = this.passwordRequirements.findIndex(req => !req.fulfilled);
                    this.password.new.isInvalid = unfulfilledReq !== -1;
                    break;
                case "CONFIRMATION": // Confirmation must match new password
                    this.password.confirmation.isInvalid = this.password.new.value !== this.password.confirmation.value;
                    break;
                default:
                    break;
            }
        },
        changePassword: async function(){
            try {
                if (this.isUserLoggedIn){ // Reset password by a logged in user
                    const payload = {
                        existingPasskey: this.password.current.value,
                        newPasskey: this.password.new.value
                    };
    
                    const response = await this.axios.put("/api/changePassword", payload);
    
                    this.displayActionConfirmation({ shown: true, message: "Password changed" });
                    
                    // Reset the fields to empty
                    for (let field in this.password){
                        this.password[field] = "";
                    }
    
                    // Reset the state of the fulfilled password requirements
                    this.passwordRequirements.forEach(req => {
                        req.fulfilled = false;
                    });
                } else { // Reset password without being logged in
                    const payload = {
                        token: this.$route.query.response,
                        passkey: this.password.new.value
                    };

                    const response = await this.axios.post('/api/resetPassword', payload);
                    this.$emit("passwordResetCompleted");
                }
            } catch (ex){
                if (this.isUserLoggedIn && ex.response.status === 400) // User has entered incorrect current password
                    this.password.current.isInvalid = true;
                else 
                    this.connectionError = true; // Notify to user that there is a server error
            }
        }
    }
}
</script>

<style lang="scss" scoped>
/* Hide the exclamation error on password validation */
label {

    &.error,
    &.match-error {

        & + input {

            & + .invalid-input,
            & + .non-matching-passwords {
    
                .fa-exclamation {
                    display: none;
                }
            }
        }
    }
}

.form-group {
    position: relative;

    input.form-control {
        padding-right: 2.5rem;
    }

    .password-visibility {
        cursor: pointer;
        position: absolute;
        right: 0.8rem;
        top: 2.5rem;
    }
}
</style>